import React, { useState } from "react";
import "./PricingCard.css";
import LoginModal from "../LoginModal";
import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import "../StripePaymentCard/StripePaymentCard.css";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Button, Col, Row } from "react-bootstrap";
import { createSubscriptionAPI } from "../../utils/requests/proposals";
import { toastError, toastSuccess } from "../../utils/helpers/helper";
import Card from "react-bootstrap/Card";
import { getUserDetail, updateUserTrade } from "../../store/actions/users/users.actions";
import { useNavigate } from "react-router-dom";

const useOptions = () => {
  return {
    style: {
      base: {
        iconColor: "#c4f0ff",
        color: "#000",
        fontWeight: "500",
        fontFamily: "Roboto, Open Sans, Segoe UI, sans-serif",
        fontSize: "16px",
        fontSmoothing: "antialiased",
        ":-webkit-autofill": {
          color: "#fce883",
        },
        "::placeholder": {
          color: "#87BBFD",
        },
      },
      invalid: {
        iconColor: "#eb1c26",
        color: "#eb1c26",
      },
    },
  };
};

const PricingCard = ({ num, product, profile }) => {
  const [loginModalShow, setLoginModalShow] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const stripe = useStripe();
  const options = useOptions();
  const { user } = useSelector((state) => state.auth);

  const handleSubmit = async (event) => {
    setError(null);
    event.preventDefault();
    if (!user) {
      setLoginModalShow(true);
      return;
    }

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      return;
    }

    if (error) {
      elements.getElement("card").focus();
      return;
    }

    const payload = await stripe.createPaymentMethod({
      type: "card",
      card: elements.getElement(CardElement),
    });
    if (payload.error) {
      setError(payload.error);
    } else {
      setLoading(true);
      let data = {
        payment_method: payload.paymentMethod.id,
        price_id: product.price_id,
        billing_cycle: product.billingCycle,
        ...user,
      };

      const isBundlePlan = product.key === 'cleaning_bid_plan' || product.key === 'painting_bid_plan' || product.key === 'all_apps';
      if (isBundlePlan) {
        const existingSub = profile?.subscriptions?.find(sub => {
          const isOppositeCycle = (product.billingCycle === 'annual' && sub.metadata?.price_id === product.monthlyPriceId) ||
                                 (product.billingCycle === 'monthly' && sub.metadata?.price_id === product.annualPriceId);
          return isOppositeCycle && sub.status === 'active';
        });

        if (existingSub) {
          data.subscription_id = existingSub.id;
          data.is_bundle_plan = true;
        }
      }

      if (product.key == 'deals_credit') {
        data = {
          deals_credit: true,
          ...data,
        }
      } else if (product.key == 'credit') {
        data = {
          amount: product.price,
          credit: true,
          ...data,
        }
      }

      createSubscriptionAPI(data)
        .then(async (response) => {
          toastSuccess("Payment is Successful");

          localStorage.setItem("user", JSON.stringify(response));
          
          // Update trade based on bundle plan purchase
          if (product.key === 'cleaning_bid_plan') {
            dispatch(updateUserTrade('cleaning'));
          } else if (product.key === 'painting_bid_plan') {
            dispatch(updateUserTrade('painting'));
          }
          
          dispatch(getUserDetail())
          navigate('/');
        })
        .catch((error) => {
          setLoading(false);
          toastError(`Payment Failed`);
        });
      // dispatch(
      //   createSubscription({
      //     payment_method: payload.paymentMethod.id,
      //     price_id: process.env.REACT_APP_STRIPE_PRICE_ID,
      //     ...user,
      //   })
      // );
      setLoading(true);
    }
  };
  let plans = [];
  if (user) {
    plans = profile?.subscriptions?.filter((subscription) => {
      if (!subscription) {
        return [];
      }

      return (
        (subscription.status === "active" &&
          product.price_id !== subscription?.metadata.price_id) ||
        (product.price_id === subscription?.metadata.price_id &&
          subscription.status === "active")
      );
    });
    plans = plans === undefined ? [] : plans;
  }

  const isSubscribed = plans.some(plan => 
    plan?.metadata?.price_id === product.price_id && plan.status === "active"
  );

  // Check if user has any subscription for bundle plans (monthly or annual)
  const isBundlePlan = product.key === 'cleaning_bid_plan' || product.key === 'painting_bid_plan';
  const hasBundleSubscription = isBundlePlan && 
    profile?.subscriptions?.some(sub => {
      const isSameBundle = (product.key === 'cleaning_bid_plan' && 
        (sub.metadata?.price_id?.includes('CLEANING_BID_PLAN_MONTHLY') || 
         sub.metadata?.price_id?.includes('CLEANING_BID_PLAN_ANNUAL'))) ||
        (product.key === 'painting_bid_plan' && 
        (sub.metadata?.price_id?.includes('PAINTING_BID_PLAN_MONTHLY') || 
         sub.metadata?.price_id?.includes('PAINTING_BID_PLAN_ANNUAL')));
      return isSameBundle && sub.status === 'active';
    });

  return (
    <>
      <form onSubmit={handleSubmit}>
        {user && (isSubscribed || hasBundleSubscription) ? (
          <div className="text-center">
            <Button variant="success" disabled className="w-100">
              ✓ Subscribed
            </Button>
          </div>
        ) : (
          <>
            <div className="mb-3">
              <label className="form-label small">Card details</label>
              {error && <Alert variant="danger" className="small">{error.message}</Alert>}
              <CardElement
                options={options}
                onChange={(e) => {
                  setError(e.error);
                }}
              />
            </div>
            <Button
              type="submit"
              variant="primary"
              className="w-100 fs-5"
              disabled={!stripe || loading}
            >
              {loading ? "Processing..." : `Subscribe - $${product.price}/${product.billingCycle === 'annual' ? 'year' : 'month'}`}
            </Button>
          </>
        )}
      </form>
      <LoginModal
        show={loginModalShow}
        onHide={() => setLoginModalShow(false)}
      />
    </>
  );
};

export default PricingCard;
